import Cookies from 'js-cookie'
import { stores, euCountries, euroCountries } from './resources/store-config'
import { Logger } from '~/plugins/log'

const logger = new Logger()
logger.setContext('Store')

const getDefaultState = () => ({
  subdomain: process.env.OVERRIDE_SUBDOMAIN || 'www',
  hasCheckedSubdomain: false,
  country: 'GB',
  shippingCountry: 'GB',
  locale: 'en-GB',
  email: null,
  frontendId: null,
  frontendCid: null,
  sg18pid: null,
  showKlarnaMessaging: true,
  shippingList: {},
  ipAddress: null,
  shippingCutoff: 15,
  shippingGuaranteed: true,
  shippingNonShipDays: [],
  shippingNonDespatchDays: {},
  season: 'summer',
  countries: []
})

export const state = getDefaultState

export const mutations = {
  RESET (state) {
    Object.assign(state, getDefaultState())
  },
  SET_FRONTEND_ID (state, frontendId) {
    state.frontendId = (frontendId || state.frontendId || '').split(',').pop()
    if (state.frontendIid === 'deleted' || state.frontendIid === 'null') state.frontendIid = null
  },
  SET_FRONTEND_CID (state, frontendCid) {
    state.frontendCid = (frontendCid || state.frontendCid || '').split(',').pop()
    if (state.frontendCid === 'deleted' || state.frontendCid === 'null') state.frontendCid = null
  },
  SET_SG18PID (state, sg18pid) {
    state.sg18pid = (sg18pid || state.sg18pid).split(',').pop()
  },
  CREATE_SG18PID (state) {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    const sessionId = Array(20).join().split(',').map(
      () => chars.charAt(Math.floor(Math.random() * chars.length))
    ).join('')

    state.sg18pid = sessionId
    return sessionId
  },
  SET_SHIPPING_LIST (state, shippingList) {
    state.shippingList = Object.fromEntries(shippingList.map(
      destination => [destination.country_iso, {
        country: destination.country_name,
        long_name: [destination.shipping_method || null, destination.shipping_service].join(' '),
        method: destination.method,
        transit: destination.transit_time,
        price: destination.price
      }]
    ))
  },
  CLEAR_SESSION (state) {
    state.frontendId = null
    state.frontendCid = null
    Cookies.remove('frontend')
    Cookies.remove('frontend-cid')
    logger.debug('CLEAR_SESSION')
  },
  CHANGE_SHIPPING_COUNTRY (state, country) {
    state.shippingCountry = country || state.shippingCountry || 'GB'
  },
  HAS_CHECKED_SUBDOMAIN (state) {
    state.hasCheckedSubdomain = true
  },
  SET_SUBDOMAIN (state, subdomain) {
    state.subdomain = subdomain
  },
  SET_IP_ADDRESS (state, ipAddress) {
    state.ipAddress = ipAddress
  },
  SET_EMAIL (state, email) {
    state.email = email || state.email
  },
  SET_SHIPPING_CONFIG (state, response) {
    state.shippingCutoff = response.cutoff || state.shippingCutoff
    state.shippingGuaranteed = response.guarantee || state.shippingGuaranteed
    state.shippingNonDespatchDays = response.non_despatch_days || state.shippingNonDespatchDays
    state.shippingNonShipDays = response.non_ship_days || state.shippingNonShipDays
  },
  SET_COUNTRIES (state, response) {
    state.countries = response.results || state.countries
  },
  SET_COUNTRY (state, country) {
    state.country = country
  },
  SET_SEASON (state, season) {
    state.season = season
  },
  SET_SHOW_KLARNA_MESSAGING (state, active) {
    state.showKlarnaMessaging = active
  }
}

export const getters = {
  storeRunCode: (state, getters) => {
    if (getters.store === 'sungod' || getters.store === 'www') return ''
    return getters.store
  },
  store: (state, getters) => (Object.entries(stores).find(
    ([store, details]) => details.subdomains.includes(getters.subdomain)
  ) || ['sungod'])[0],
  websiteId: (state, getters) => (getters.stores[getters.store] || {}).websiteId || 2,
  hasCheckedSubdomain: state => state.hasCheckedSubdomain,
  stores: () => stores,
  email: state => state.email,
  country: state => state.country,
  subdomain: state => state.subdomain,
  subdomains: () => Object.values(stores).map(store => store.subdomains).flat(),
  currency: (state, getters) => stores[getters.store].currency,
  currencySymbol: (state, getters) => stores[getters.store].symbol,
  flag: (state, getters) => stores[getters.store].flag,
  locale: state => state.locale,
  showKlarnaMessaging: (state, getters, rootState, rootGetters) => rootGetters['flag/isActive']('website.klarna_payment_messaging'),
  frontendId: (state) => {
    return state.frontendId
  },
  frontendCid: state => state.frontendCid,
  sg18pid: (state) => {
    if (state.sg18pid) return state.sg18pid.split(',').pop()
    return null
  },
  shippingCountries: state => Object.entries(state.shippingList).map(
    ([iso, country]) => { return { ...country, iso } }
  ).sort((a, b) => a.country.localeCompare(b.country)),
  validCountryCode: state => iso => iso in state.shippingList,
  countryNameFromCode: state => iso => (state.shippingList[iso] || {}).country,
  shippingCountry: state => (state.shippingCountry || 'GB').toUpperCase(),
  shippingCountryName: (state, getters) => getters.countryNameFromCode(getters.shippingCountry),
  shippingDetails: (state, getters) => ({
    isDomestic: getters.isShippingDomestic,
    domesticCost: getters.shippingCostDomestic,
    internationalCost: getters.shippingCostInternational,
    ...(state.shippingList[getters.shippingCountry] || {})
  }),
  shippingCostDomestic: (state, getters) => stores[getters.store].shipping.domestic,
  shippingCostInternational: (state, getters) => stores[getters.store].shipping.international,
  shippingCostLetter: (state, getters) => stores[getters.store].shipping.letter,
  isShippingDomestic (state, getters) {
    return getters.shippingCountry.toLowerCase() === 'gb'
  },
  shippingCost: (state, getters, rootState, rootGetters) => {
    const details = getters.shippingDetails
    if (rootGetters['promo/shippingPromoActive']) return 0
    if (rootGetters['cart/onlyDigitalGiftCards']) return 0
    if (rootGetters['cart/onlyGiftCards']) return getters.shippingCostLetter
    if (details.price) return details.price // Return price provided by the database if we have it

    // Otherwise return the statically defined prices
    if (details.isDomestic) return details.domesticCost
    return details.internationalCost
  },
  shippingCutoff: state => state.shippingCutoff,
  shippingGuaranteed: state => state.shippingGuaranteed,
  shippingNonShipDays: state => state.shippingNonShipDays,
  shippingNonDespatchDays: state => state.shippingNonDespatchDays,
  season: state => state.season,
  ipAddress: state => state.ipAddress,
  countries: (state, getters) => state.countries.map((country) => {
    country.name = getters.countryNameFromCode(country.iso2_code)
    return country
  }).filter(
    country => country.name
  ).sort(
    (a, b) => (a.name || '').localeCompare((b.name || ''))
  ),
  regions: state => countryCode => (state.countries.find(country => country.iso2_code === countryCode) || {}).regions || [],
  regionId: state => (countryCode, name) => ((state.countries.find(country => country.iso2_code === countryCode || {}).regions || []).find(region => region.default_name === name) || {}).region_id || null,
  euroCountries: () => euroCountries,
  euCountries: () => euCountries,
  isUK: (state, getters) => (getters.country || '').toUpperCase() === 'GB',
  isEU: (state, getters) => !getters.isUK && getters.euCountries.includes((getters.country || '').toUpperCase()),
  isROW: (state, getters) => !getters.isEU && !getters.isUK
}

export const actions = {
  async GET_SHIPPING_LIST ({ commit, state, getters }) {
    const { data } = await this.$publicAPI.quietGet('shipping/', {
      params: { limit: 500, website: getters.websiteId },
      useCache: true
    })

    commit('SET_SHIPPING_LIST', data.results)

    logger.debug('GET_SHIPPING_LIST succeeded')
    return data
  },

  async CHANGE_STORE ({ commit, state, dispatch, getters }, [target, discountCode]) {
    const targetSubdomain = target === 'sungod' ? 'www' : target
    
    logger.debug('NOT IMPLEMENTED - check if store-restricted coupon is applied')
    if (getters.subdomain === targetSubdomain) return
    logger.debug(`Changing subdomain from ${getters.subdomain} to ${targetSubdomain} (SC:${target})`)

    const oldSubdomain = getters.subdomain
    const oldStoreCode = getters.storeRunCode

    commit('page/REQUEST_START', [-1, 1e6], { root: true })
    await dispatch('cart/CHANGE_STORE', [oldStoreCode, target], { root: true })
    await dispatch('session/UPDATE', { country: targetSubdomain }, { root: true })
    
    commit('cart/CLEAR_SESSION', null, { root: true })
    commit('checkout/CLEAR_SESSION', null, { root: true })

    const redirect = new URL(window.location.href)
    redirect.host = redirect.host.replace(new RegExp(`^${oldSubdomain}.`, 'gi'), `${targetSubdomain}.`)
    if (discountCode) redirect.searchParams.append('applyCode', discountCode)

    redirect.searchParams.delete('country')
    redirect.searchParams.append('country', targetSubdomain)
    redirect.searchParams.delete('sg18pid')
    redirect.searchParams.append('sg18pid', getters.sg18pid)

    window.location.href = redirect.toString()
  },

  async GET_SHIPPING_CONFIG ({ commit, state }) {
    logger.debug('GET_SHIPPING_CONFIG')
    const { data } = await this.$publicAPI.quietGet('config/shipping/', { useCache: true })
    commit('SET_SHIPPING_CONFIG', data)
    logger.debug('GET_SHIPPING_CONFIG - done')
  },

  async GET_COUNTRIES ({ commit, state }) {
    logger.debug('GET_COUNTRIES')
    const { data } = await this.$publicAPI.get('countries/?limit=500', { useCache: true })
    commit('SET_COUNTRIES', data)
    logger.debug('GET_COUNTRIES - done')
  }
}
